/* A QR encoder, because the alternative was a dependency for one screen.
 *
 * It draws exactly one kind of thing: a short https URL, in byte mode, at
 * error-correction level M, on a card a manager prints or holds up. Versions 1
 * to 6 — 106 bytes — which is three times the longest enrollment link this
 * product can produce and stops short of version 7, where the symbol starts
 * carrying an eighteen-bit version block that nothing here would ever exercise
 * and nothing here could therefore be trusted to get right.
 *
 * Client-side for a reason that is not convenience: a server-rendered QR is a
 * fetchable object whose URL contains, or can be traded for, a live enrollment
 * code. This one exists as a path in an SVG for as long as the screen is open
 * and is never an address.
 *
 * The mask is chosen by the penalty rules rather than fixed. A fixed mask is
 * the shortcut every small implementation takes and it is the one that fails in
 * the field: the wrong mask on the wrong payload leaves large blank runs that a
 * phone camera on a wet walkway will not lock onto, and it fails for one code
 * and not the next, which reads as "the scanner is unreliable".
 *
 * CHECKED AGAINST libqrencode, not against itself. Three bugs here survived a
 * round-trip test in which this file both wrote and read the symbol -- a
 * transposed format-info map that put a bit on the timing column, a
 * format-bit ordering that read back as the wrong error-correction level, and
 * a generator polynomial built in ascending degree, which produced valid-
 * looking check codewords that protect nothing. All three decode perfectly
 * against a reader making the same mistake. The test that found them compared
 * module for module against `qrencode -8 -l M`; where the two choose the same
 * mask the grids are now identical, and where they differ this one's mask
 * scores lower on the standard's own penalty rules. Any change in here should
 * be checked the same way, because "it scans on my phone" does not distinguish
 * a correct symbol from one whose error correction is decorative.
 */

/* ------------------------------------------------------------ GF(256) */

const EXP = new Uint8Array(512)
const LOG = new Uint8Array(256)
{
  let x = 1
  for (let i = 0; i < 255; i++) {
    EXP[i] = x
    LOG[x] = i
    x <<= 1
    if (x & 0x100) x ^= 0x11d      // the QR primitive polynomial
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!
}

const mul = (a: number, b: number) =>
  a === 0 || b === 0 ? 0 : EXP[LOG[a]! + LOG[b]!]!

/** The generator polynomial for `n` error-correction codewords.
 *
 *  Coefficients in DESCENDING degree, so `g[0]` is the leading 1 and `g[1..n]`
 *  is what the division below consumes. The two lines inside the loop are the
 *  whole of the multiplication by (x + a^i), and swapping them silently
 *  produces the same polynomial written backwards -- which still divides, still
 *  yields a symbol, and yields one whose error correction is nonsense. Checked
 *  against the published table: gen(10) is 1,216,194,159,111,199,94,95,113,
 *  157,193.
 */
function generator(n: number): number[] {
  let g = [1]
  for (let i = 0; i < n; i++) {
    const next = new Array<number>(g.length + 1).fill(0)
    for (let j = 0; j < g.length; j++) {
      next[j] = next[j]! ^ g[j]!                       // g(x) * x
      next[j + 1] = next[j + 1]! ^ mul(g[j]!, EXP[i]!) // g(x) * a^i
    }
    g = next
  }
  return g
}

/** Reed-Solomon remainder: the `n` check codewords for one block. */
function remainder(data: number[], n: number): number[] {
  const g = generator(n)
  const out = new Array<number>(n).fill(0)
  for (const byte of data) {
    const factor = byte ^ out[0]!
    out.shift()
    out.push(0)
    if (factor !== 0) for (let i = 0; i < n; i++) out[i] = out[i]! ^ mul(g[i + 1]!, factor)
  }
  return out
}

/* --------------------------------------------------------- the tables
 *
 * Level M only. Each row is [total codewords, ec codewords per block,
 * block sizes as data codewords]. Written out rather than derived: the
 * derivation is a second thing to get wrong and these five rows are the whole
 * of what this file supports.
 */
const VERSIONS: Record<number, { ec: number; blocks: number[] }> = {
  1: { ec: 10, blocks: [16] },
  2: { ec: 16, blocks: [28] },
  3: { ec: 26, blocks: [44] },
  4: { ec: 18, blocks: [32, 32] },
  5: { ec: 24, blocks: [43, 43] },
  6: { ec: 16, blocks: [27, 27, 27, 27] },
}

// Row and column centres of the alignment patterns. Version 1 has none.
const ALIGN: Record<number, number[]> = {
  1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30], 6: [6, 34],
}

const MAX_VERSION = 6

/* -------------------------------------------------------------- encode */

/** The payload as a bit string: mode, length, bytes, terminator, padding. */
function bits(data: Uint8Array, dataCodewords: number): number[] {
  const out: number[] = []
  const push = (value: number, width: number) => {
    for (let i = width - 1; i >= 0; i--) out.push((value >> i) & 1)
  }
  push(0b0100, 4)                  // byte mode
  push(data.length, 8)             // one length byte, versions 1-9
  for (const b of data) push(b, 8)

  const capacity = dataCodewords * 8
  push(0, Math.min(4, capacity - out.length))   // terminator
  while (out.length % 8) out.push(0)            // to a byte boundary

  // 0xEC 0x11, alternating, for ever. Specified, not arbitrary: a decoder
  // that finds something else here has found a corrupt symbol.
  const pad = [0xec, 0x11]
  for (let i = 0; out.length < capacity; i++) push(pad[i % 2]!, 8)
  return out
}

/** Data and check codewords, interleaved the way the standard requires.
 *
 *  The interleaving is what makes the error correction worth having: a thumb
 *  over one corner damages a few codewords of every block rather than
 *  destroying one block outright, and each block can absorb its share.
 */
function codewords(data: Uint8Array, version: number): number[] {
  const { ec, blocks } = VERSIONS[version]!
  const stream = bits(data, blocks.reduce((a, b) => a + b, 0))

  const bytes: number[] = []
  for (let i = 0; i < stream.length; i += 8) {
    let v = 0
    for (let j = 0; j < 8; j++) v = (v << 1) | stream[i + j]!
    bytes.push(v)
  }

  const dataBlocks: number[][] = []
  const ecBlocks: number[][] = []
  let at = 0
  for (const size of blocks) {
    const block = bytes.slice(at, at + size)
    at += size
    dataBlocks.push(block)
    ecBlocks.push(remainder(block, ec))
  }

  const out: number[] = []
  const widest = Math.max(...blocks)
  for (let i = 0; i < widest; i++) {
    for (const b of dataBlocks) if (i < b.length) out.push(b[i]!)
  }
  for (let i = 0; i < ec; i++) for (const b of ecBlocks) out.push(b[i]!)
  return out
}

/* -------------------------------------------------------------- the grid */

type Grid = { size: number; on: Uint8Array; fixed: Uint8Array }

function blank(version: number): Grid {
  const size = version * 4 + 17
  return { size, on: new Uint8Array(size * size), fixed: new Uint8Array(size * size) }
}

function set(g: Grid, r: number, c: number, on: boolean, fixed = true) {
  if (r < 0 || c < 0 || r >= g.size || c >= g.size) return
  g.on[r * g.size + c] = on ? 1 : 0
  if (fixed) g.fixed[r * g.size + c] = 1
}

const at = (g: Grid, r: number, c: number) => g.on[r * g.size + c] === 1

/** The three corner squares, and the quiet separator around each. */
function finders(g: Grid) {
  for (const [r0, c0] of [[0, 0], [0, g.size - 7], [g.size - 7, 0]] as const) {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const inner = r >= 2 && r <= 4 && c >= 2 && c <= 4
        const ring = r === 0 || r === 6 || c === 0 || c === 6
        const outside = r < 0 || r > 6 || c < 0 || c > 6
        set(g, r0 + r, c0 + c, !outside && (ring || inner))
      }
    }
  }
}

function alignment(g: Grid, version: number) {
  const centres = ALIGN[version]!
  for (const r0 of centres) {
    for (const c0 of centres) {
      // Not over a finder: the three corners already carry a pattern.
      const nearFinder =
        (r0 <= 8 && c0 <= 8) ||
        (r0 <= 8 && c0 >= g.size - 9) ||
        (r0 >= g.size - 9 && c0 <= 8)
      if (nearFinder) continue
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          set(g, r0 + r, c0 + c,
              Math.max(Math.abs(r), Math.abs(c)) !== 1)
        }
      }
    }
  }
}

function timing(g: Grid) {
  for (let i = 8; i < g.size - 8; i++) {
    set(g, 6, i, i % 2 === 0)
    set(g, i, 6, i % 2 === 0)
  }
}

/* Where the fifteen format bits live, most significant first.
 *
 * Two tables rather than arithmetic, and that is not laziness. Written as
 * arithmetic this lands a bit on the timing column, which the symbol survives
 * -- it round-trips perfectly against a reader making the same mistake -- and
 * which a real scanner then refuses. These positions were checked module for
 * module against libqrencode; see the note above `encode`.
 */
const FORMAT_1: [number, number][] = [
  [8, 0], [8, 1], [8, 2], [8, 3], [8, 4], [8, 5], [8, 7], [8, 8],
  [7, 8], [5, 8], [4, 8], [3, 8], [2, 8], [1, 8], [0, 8],
]
const format2 = (n: number): [number, number][] => [
  [n - 1, 8], [n - 2, 8], [n - 3, 8], [n - 4, 8], [n - 5, 8], [n - 6, 8],
  [n - 7, 8],
  [8, n - 8], [8, n - 7], [8, n - 6], [8, n - 5], [8, n - 4], [8, n - 3],
  [8, n - 2], [8, n - 1],
]

/** Fifteen bits of BCH-protected format information, written twice.
 *
 *  Twice because it carries the mask, and without the mask the symbol is
 *  noise: a thumb over the top-left corner would otherwise cost the whole
 *  code rather than the few codewords the error correction exists to absorb.
 */
function format(g: Grid, mask: number) {
  const value = (0b00 << 3) | mask         // 00 is level M
  let bch = value << 10
  for (let i = 4; i >= 0; i--) if (bch & (1 << (i + 10))) bch ^= 0b10100110111 << i
  const f = ((value << 10) | bch) ^ 0b101010000010010

  const second = format2(g.size)
  for (let k = 0; k < 15; k++) {
    // k counts from the most significant bit, which is the order both tables
    // are written in.
    const bit = ((f >> (14 - k)) & 1) === 1
    set(g, FORMAT_1[k]![0], FORMAT_1[k]![1], bit)
    set(g, second[k]![0], second[k]![1], bit)
  }
  set(g, g.size - 8, 8, true)              // the always-dark module
}


/** Lay the codeword bits out in the two-wide upward-and-downward snake. */
function place(g: Grid, words: number[]) {
  let i = 0
  let up = true
  for (let right = g.size - 1; right > 0; right -= 2) {
    if (right === 6) right--             // the vertical timing column is not data
    for (let step = 0; step < g.size; step++) {
      const row = up ? g.size - 1 - step : step
      for (const col of [right, right - 1]) {
        if (g.fixed[row * g.size + col]) continue
        const bit = i < words.length * 8
          ? (words[i >> 3]! >> (7 - (i & 7))) & 1
          : 0
        i++
        set(g, row, col, bit === 1, false)
      }
    }
    up = !up
  }
}

const MASKS: ((r: number, c: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
]

/** The four penalty rules, summed. Lower is easier to scan. */
function penalty(g: Grid): number {
  const n = g.size
  let score = 0

  // 1. Runs of five or more of the same colour, in both directions.
  for (let i = 0; i < n; i++) {
    for (const row of [true, false]) {
      let run = 1
      for (let j = 1; j < n; j++) {
        const a = row ? at(g, i, j) : at(g, j, i)
        const b = row ? at(g, i, j - 1) : at(g, j - 1, i)
        if (a === b) { run++; if (run === 5) score += 3; else if (run > 5) score++ }
        else run = 1
      }
    }
  }

  // 2. Every 2x2 block of one colour.
  for (let r = 0; r < n - 1; r++) {
    for (let c = 0; c < n - 1; c++) {
      const v = at(g, r, c)
      if (v === at(g, r, c + 1) && v === at(g, r + 1, c) && v === at(g, r + 1, c + 1)) {
        score += 3
      }
    }
  }

  // 3. The finder-like sequence, which a decoder would mistake for a corner.
  const A = [true, false, true, true, true, false, true, false, false, false, false]
  const B = [false, false, false, false, true, false, true, true, true, false, true]
  for (let i = 0; i < n; i++) {
    for (let j = 0; j + 11 <= n; j++) {
      for (const row of [true, false]) {
        let ma = true
        let mb = true
        for (let k = 0; k < 11; k++) {
          const v = row ? at(g, i, j + k) : at(g, j + k, i)
          if (v !== A[k]) ma = false
          if (v !== B[k]) mb = false
        }
        if (ma) score += 40
        if (mb) score += 40
      }
    }
  }

  // 4. How far the light/dark balance is from even.
  let dark = 0
  for (let i = 0; i < n * n; i++) if (g.on[i]) dark++
  score += Math.floor(Math.abs((dark * 100) / (n * n) - 50) / 5) * 10
  return score
}

/** The finished module grid: `true` is a dark module. */
export function encode(text: string): boolean[][] {
  const data = new TextEncoder().encode(text)

  let version = 0
  for (let v = 1; v <= MAX_VERSION; v++) {
    const total = VERSIONS[v]!.blocks.reduce((a, b) => a + b, 0)
    if (data.length + 2 <= total) { version = v; break }
  }
  if (!version) {
    throw new Error(`${data.length} bytes will not fit in a version-${MAX_VERSION} symbol`)
  }

  const words = codewords(data, version)

  let best: Grid | null = null
  let bestScore = Infinity
  for (let mask = 0; mask < 8; mask++) {
    const g = blank(version)
    finders(g)
    alignment(g, version)
    timing(g)
    // Reserved before the data is laid out, so `place` skips these cells --
    // then written for real, because the mask is not chosen yet.
    format(g, 0)
    place(g, words)
    for (let r = 0; r < g.size; r++) {
      for (let c = 0; c < g.size; c++) {
        const i = r * g.size + c
        if (!g.fixed[i] && MASKS[mask]!(r, c)) g.on[i] = g.on[i]! ^ 1
      }
    }
    format(g, mask)
    const s = penalty(g)
    if (s < bestScore) { bestScore = s; best = g }
  }

  const g = best!
  const out: boolean[][] = []
  for (let r = 0; r < g.size; r++) {
    const row: boolean[] = []
    for (let c = 0; c < g.size; c++) row.push(at(g, r, c))
    out.push(row)
  }
  return out
}
